import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import type { QueryConstraint } from "@/core";

/**
 * Shared helpers for the date-range filters on the operations list pages
 * (sell orders, purchase orders, transactions, finance).
 *
 * Boundaries are local-time day edges, so picking a day includes everything
 * dated that day in the operator's own timezone.
 */

export function startOfDay(value: Date): Date {
  const start = new Date(value);
  start.setHours(0, 0, 0, 0);
  return start;
}

export function endOfDay(value: Date): Date {
  const end = new Date(value);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Firestore constraints bounding `field` to the selected window.
 *
 * Range and orderBy sit on the same field, so this needs no composite index —
 * as long as the caller orders by that same field.
 */
export function buildDateRangeConstraints<TEntity>(
  range: DateRange | undefined,
  field: keyof TEntity & string,
): QueryConstraint<keyof TEntity & string>[] {
  const constraints: QueryConstraint<keyof TEntity & string>[] = [];

  if (range?.from) {
    constraints.push({ field, operator: ">=", value: startOfDay(range.from) });
  }

  const rangeEnd = range?.to || range?.from;
  if (rangeEnd) {
    constraints.push({ field, operator: "<=", value: endOfDay(rangeEnd) });
  }

  return constraints;
}

export function formatDateRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) {
    return "All dates";
  }

  if (!range.to) {
    return format(range.from, "yyyy-MM-dd");
  }

  return `${format(range.from, "yyyy-MM-dd")} - ${format(range.to, "yyyy-MM-dd")}`;
}

/** Filename suffix so exports of different windows don't overwrite each other. */
export function buildExportSuffix(range: DateRange | undefined): string {
  if (!range?.from) {
    return `all-${format(new Date(), "yyyy-MM-dd")}`;
  }

  const from = format(range.from, "yyyy-MM-dd");
  return range.to ? `${from}_to_${format(range.to, "yyyy-MM-dd")}` : from;
}

/** Quick presets offered alongside the calendar picker. */
export function monthToDateRange(): DateRange {
  const now = new Date();
  return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
}

export function yearToDateRange(): DateRange {
  const now = new Date();
  return { from: new Date(now.getFullYear(), 0, 1), to: now };
}
